import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../services/axiosInstance';

const fetchAllStockLevels = async (): Promise<Record<string, number>> => {
  try {
    const response = await axiosInstance.get(`/api/cart/store-items`);
    const map: Record<string, number> = {};
    for (const item of response.data) {
      map[item.assetId] = item.stockLevel;
    }
    return map;
  } catch (error) {
    console.error('Error fetching stock levels:', error);
    return {};
  }
};

export function useStockLevels(
  hasStoreAssets: boolean,
  onCartChange: (cartItems: Record<string, number>) => void
) {
  const [cartItems, setCartItems] = useState<Record<string, number>>({});
  const [stockLevels, setStockLevels] = useState<Record<string, number>>({});
  const [stockLevelsLoading, setStockLevelsLoading] = useState(false);

  const refreshStockLevels = useCallback(async () => {
    setStockLevelsLoading(true);
    try {
      const levels = await fetchAllStockLevels();
      setStockLevels(levels);
    } finally {
      setStockLevelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasStoreAssets) return;
    refreshStockLevels();
  }, [hasStoreAssets, refreshStockLevels]);

  const addToCart = async (assetId: string) => {
    try {
      const response = await axiosInstance.post(`/api/cart/add`, {
        assetId,
        quantity: 1
      });

      if (response.status === 200 && response.data) {
        const updatedCartItems = { ...cartItems };

        if (response.data.cartItem) {
          updatedCartItems[assetId] = response.data.cartItem.quantity;
        } else {
          updatedCartItems[assetId] = (cartItems[assetId] || 0) + 1;
        }

        setCartItems(updatedCartItems);
        onCartChange(updatedCartItems);

        await refreshStockLevels();
      }
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Kunde inte lägga till artikel i varukorgen');
      }
    }
  };

  const getStockColor = (stockLevel: number): string => {
    if (stockLevel === 0) return '#f44336';
    if (stockLevel <= 5) return '#ff9800';
    return '#4caf50';
  };

  return {
    cartItems,
    stockLevels,
    stockLevelsLoading,
    addToCart,
    getStockColor,
    refreshStockLevels,
  };
}
